import { ICinnamonAction } from '../interfaces/actions'
import { isObject } from './validTypes'
import { makeObjectRecursion } from './recursions'
import processData from './processData'
import type { IGenericFunction, UnresolvedRecord } from '../interfaces'
import EventEmitter from '../classes/EventEmiter'

/** no evaluate props */
const noEvaluate = ['styles', 'badge']

/**
 * Convert an object to an arrow function that performs other functions
 * @param actionsList: ICinnamonAction[]
 * @param actions: any
 * @returns unknown (value of the last action)
 */
function createFunction(
  actionsList: ICinnamonAction[],
  eventEmitter: EventEmitter,
): (callbackArgs: any) => unknown {
  return (callbackArgs: any) => {
    /*
     * Components usually call back with an object, which spreads into the
     * `this` source unchanged. Some call back with a bare scalar instead —
     * OrderList's `onOpenDetail(id)` and `onFilterChange(status)` are the
     * first — and spreading a string yields a char-indexed object, so
     * `{{this}}` resolves to `{0:'o',1:'r',...}`. Wrapping a scalar as
     * `{ value }` makes it addressable as `{{this.value}}`.
     *
     * Deliberately narrow: null and undefined still spread to `{}` and every
     * object or array path is untouched, so no existing action changes.
     */
    const isScalarArg =
      callbackArgs !== null &&
      callbackArgs !== undefined &&
      typeof callbackArgs !== 'object'
    const compData = isScalarArg ? { value: callbackArgs } : { ...callbackArgs }
    // compData is the data that component returns when its handleAction is executed
    const sources = {
      this: compData,
    }
    // execute the array of actions
    return actionsList.reduce<unknown>((acc, { name, args = {} }) => {
      const newArgs = processData(args, sources) // process {{this}}} variables
      return eventEmitter.emit(name, newArgs)
    }, undefined)
  }
}

/**
 * Check for cinnamon actions into an object
 * @param props: object
 * @param actions: any
 * @returns
 */
export const processActions = (
  props: UnresolvedRecord,
  eventEmitter: EventEmitter,
) => {
  const newProps: any = { ...props }

  Object.keys(newProps).forEach((prop: string) => {
    // if prop is not into the noEvaluate list
    if (!noEvaluate.includes(prop)) {
      const propValue = newProps[prop]

      // if value is Object or Array
      if (typeof propValue === 'object') {
        let newPropValue: any = propValue

        // if is Object and contains 'cinnamonActions'
        if (isObject(propValue) && propValue.cinnamonActions) {
          const { cinnamonActions } = propValue
          newProps[prop] = createFunction(cinnamonActions, eventEmitter)
          return // skip to next prop
        }

        // recursion
        newPropValue = makeObjectRecursion(
          propValue,
          processActions as IGenericFunction,
          [eventEmitter],
        )

        // update prop
        newProps[prop] = newPropValue
      }
    }
  })

  return newProps
}

export default processActions
